import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../Layout/Layout';
import ScheduleApp from './ScheduleApp';
import ScheduleBoundary from './ScheduleBoundary';
import { useCompanyDetails } from './useScheduleV6Gate';
import { useEntitlements } from '../../../hooks/useEntitlements';
import { useCustomToast } from '../../Contexts/ToastContext';
import { onUpgrade402 } from '../../../utils/onUpgrade402';
import { useGetThemeOptions } from '../../../queries/campaignSchedules';
import {
  fetchConflicts, fetchHoldsPreview, useDeleteRule, useEndNow, useManualChange, useOccasions, useResumeHold, useSaveRule, useScheduleOverview,
  useScheduleScreens, useStatusMany, useToggleRule,
} from '../../../queries/scheduleRules';
import { formToPayload, nowIn, ruleFromApi, serverInfo } from '../../../schedule/model';

const FALLBACK_TZ = 'Asia/Riyadh';

/** The clock of the page: the company's own time zone, ticking every 30 s. */
function useNow(tz) {
  const [ms, setMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => nowIn(tz, ms), [tz, ms]);
}

/**
 * The new «الجدول» page wired to the real API (rules overview, groups + screens, campaigns, status-many). It is mounted by
 * `ScheduleThemesRoute` only when the gate is open; the page itself is `ScheduleApp`.
 */
export default function ScheduleV6() {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const { has } = useEntitlements();
  const details = useCompanyDetails();
  const tz = details?.company?.timezone || FALLBACK_TZ;
  const now = useNow(tz);
  const pro = has('scheduling_pro');

  const overview = useScheduleOverview();
  const screensQ = useScheduleScreens();
  const campaigns = useGetThemeOptions();
  const save = useSaveRule();
  const toggle = useToggleRule();
  const remove = useDeleteRule();
  const endNow = useEndNow();
  const resume = useResumeHold();
  const manualChange = useManualChange();
  const [occCountry, setOccCountry] = useState(undefined); // undefined = the API's own default (branch / company zone)
  const occ = useOccasions(occCountry);

  const groups = useMemo(() => screensQ.data || [], [screensQ.data]);
  const items = useMemo(() => (overview.data?.rules || []).map((r) => ruleFromApi(r, groups)), [overview.data, groups]);
  const shows = campaigns.data || [];

  const standing = overview.data?.standing || [];
  const def = standing.length ? { show: standing[0].campaign_id, others: standing.slice(1).reduce((n, s) => n + s.count, 0) } : null;

  // Pro: what the backend says each screen of the chosen target plays right now
  const [target, setTarget] = useState('all');
  const body = useMemo(() => {
    if (target === 'all') return {};
    if (groups.some((g) => g.id === target)) return { group_id: target };
    return { device_ids: [target] };
  }, [target, groups]);
  const status = useStatusMany(body, pro && items.length > 0);
  const serverNow = useMemo(() => {
    const list = status.data?.screens;
    if (!list) return null;
    const byId = new Map(items.map((i) => [i.id, i]));
    return Object.fromEntries(list.map((s) => [s.id, serverInfo(s, byId)]));
  }, [status.data, items]);

  const upgrade = useCallback(() => {
    showToast('warn', t('feature_unavailable.upgrade_toast_title'), t('feature_unavailable.upgrade_toast_body'));
  }, [showToast, t]);

  const actions = useMemo(() => ({
    save: async ({ form, resolve }) => {
      try {
        await save.mutateAsync({ key: form.edit?.id, payload: formToPayload(form, { resolve }) });
        return { ok: true };
      } catch (err) {
        if (onUpgrade402(err, showToast)) return { handled: true };
        return { ok: false };
      }
    },
    toggle: async (item, on) => {
      try { await toggle.mutateAsync({ key: item.id, is_active: on }); return { ok: true }; } catch (err) { return { ok: false, handled: onUpgrade402(err, showToast) }; }
    },
    remove: async (item) => {
      try { await remove.mutateAsync(item.id); return { ok: true }; } catch (err) { return { ok: false }; }
    },
    conflicts: (form) => fetchConflicts(formToPayload(form), form.edit?.id),
    preview: fetchHoldsPreview,
    manualChange: async ({ deviceIds, campaignId }) => {
      try { await manualChange.mutateAsync({ deviceIds, campaignId }); return { ok: true }; } catch (err) { return { ok: false, handled: onUpgrade402(err, showToast) }; }
    },
    endNow: async (keys) => {
      try { await endNow.mutateAsync(keys); return { ok: true }; } catch (err) { return { ok: false }; }
    },
    resume: async (deviceIds) => {
      try { await resume.mutateAsync(deviceIds); return { ok: true }; } catch (err) { return { ok: false }; }
    },
    setCountry: setOccCountry,
    retry: () => { overview.refetch(); screensQ.refetch(); campaigns.refetch(); },
    upgrade,
  }), [save, toggle, remove, endNow, resume, manualChange, overview, screensQ, campaigns, showToast, upgrade]);

  const loading = overview.isLoading || screensQ.isLoading || campaigns.isLoading;
  const failed = overview.isError || screensQ.isError;
  const sv2 = details?.schedule_v2;

  return (
    <Layout padding={false}>
      <ScheduleBoundary onReset={actions.retry}>
        <ScheduleApp
          status={failed ? 'error' : (loading ? 'loading' : 'ready')}
          now={now}
          tz={tz}
          shows={shows}
          groups={groups}
          items={items}
          def={def}
          serverNow={serverNow}
          pro={pro}
          minVersion={sv2?.min_version_code ?? null}
          oldScreens={sv2?.min_version_code ? (sv2.devices_below_min || 0) : 0}
          occasions={{ country: occ.data?.country ?? occCountry ?? '', items: occ.data?.items || [], loading: occ.isLoading, failed: occ.isError }}
          actions={actions}
          onTarget={setTarget}
        />
      </ScheduleBoundary>
    </Layout>
  );
}
